import React, { useState } from 'react';
import { Modal, Button as BsButton, Form, InputGroup } from 'react-bootstrap';
import Button from 'components/base/Button';
import {
  CreateApiTokenRequest,
  DEFAULT_TOKEN_TTL_DAYS,
  MAX_API_TOKEN_DESCRIPTION_LENGTH,
  MAX_TOKEN_TTL_DAYS,
  TOKEN_LIFETIME_OPTIONS,
} from 'models/ApiToken';

type ApiTokenAddFormProps = {
  show: boolean;
  handleClose: () => void;
  handleSave: (data: CreateApiTokenRequest) => Promise<void>;
};

const ApiTokenAddForm: React.FC<ApiTokenAddFormProps> = ({
  show,
  handleClose,
  handleSave,
}) => {
  const [description, setDescription] = useState('');
  const [expiresInDays, setExpiresInDays] = useState(DEFAULT_TOKEN_TTL_DAYS);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  const resetForm = () => {
    setDescription('');
    setExpiresInDays(DEFAULT_TOKEN_TTL_DAYS);
    setError(null);
    setFieldErrors({});
    setSubmitting(false);
  };

  const handleCloseAndReset = () => {
    resetForm();
    handleClose();
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    setFieldErrors({});

    const trimmedDescription = description.trim();

    if (!trimmedDescription) {
      setFieldErrors({
        description: ['Description is required.'],
      });
      setSubmitting(false);
      return;
    }

    if (trimmedDescription.length > MAX_API_TOKEN_DESCRIPTION_LENGTH) {
      setFieldErrors({
        description: [
          `Description must be at most ${MAX_API_TOKEN_DESCRIPTION_LENGTH} characters.`,
        ],
      });
      setSubmitting(false);
      return;
    }

    if (expiresInDays < 1 || expiresInDays > MAX_TOKEN_TTL_DAYS) {
      setFieldErrors({
        expires_in_days: [`Token lifetime must be between 1 and ${MAX_TOKEN_TTL_DAYS} days.`],
      });
      setSubmitting(false);
      return;
    }

    const payload: CreateApiTokenRequest = {
      description: trimmedDescription,
      expires_in_days: expiresInDays,
    };

    try {
      await handleSave(payload);
      handleCloseAndReset();
    } catch (err: unknown) {
      const validationErrors = (err as Error & { validationErrors?: Record<string, string[]> })
        .validationErrors;
      if (validationErrors) {
        setFieldErrors(validationErrors);
      }
      setError(err instanceof Error ? err.message : 'Failed to create API token');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting || !description.trim()) return;
    void handleSubmit();
  };

  return (
    <Modal show={show} onHide={handleCloseAndReset} centered>
      <Form onSubmit={handleFormSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Create API Token</Modal.Title>
        </Modal.Header>
        <Modal.Body>
        {error && (
          <div className="alert alert-outline-danger mb-3" role="alert">
            {error}
          </div>
        )}
        <Form.Group controlId="apiTokenDescription" className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={MAX_API_TOKEN_DESCRIPTION_LENGTH}
              isInvalid={Boolean(fieldErrors.description)}
              aria-describedby="apiTokenDescriptionHelp"
            />
            {fieldErrors.description && (
              <Form.Control.Feedback type="invalid">
                {fieldErrors.description.join(' ')}
              </Form.Control.Feedback>
            )}
            <Form.Text id="apiTokenDescriptionHelp" className="text-muted">
              {description.length}/{MAX_API_TOKEN_DESCRIPTION_LENGTH} characters
            </Form.Text>
          </Form.Group>
          <Form.Group controlId="apiTokenExpiresInDays">
            <Form.Label>Token lifetime (days)</Form.Label>
            <InputGroup hasValidation className="mb-2">
              <Form.Control
                type="number"
                inputMode="numeric"
                min={1}
                max={MAX_TOKEN_TTL_DAYS}
                value={expiresInDays}
                onChange={(e) => {
                  const parsed = parseInt(e.target.value, 10);
                  if (!Number.isNaN(parsed)) {
                    setExpiresInDays(parsed);
                  }
                }}
                isInvalid={Boolean(fieldErrors.expires_in_days)}
                aria-describedby="apiTokenExpiresInDaysHelp"
              />
              <InputGroup.Text>days</InputGroup.Text>
              {fieldErrors.expires_in_days && (
                <Form.Control.Feedback type="invalid">
                  {fieldErrors.expires_in_days.join(' ')}
                </Form.Control.Feedback>
              )}
            </InputGroup>
            <div className="d-flex flex-wrap gap-2 mb-2">
              {TOKEN_LIFETIME_OPTIONS.map((days) => (
                <BsButton
                  key={days}
                  type="button"
                  size="sm"
                  variant={expiresInDays === days ? 'primary' : 'outline-secondary'}
                  className="px-3"
                  onClick={() => setExpiresInDays(days)}
                >
                  {days}
                </BsButton>
              ))}
            </div>
            <Form.Text id="apiTokenExpiresInDaysHelp" className="text-muted">
              Enter a value from 1 to {MAX_TOKEN_TTL_DAYS}. Exact expiry time is set when the
              token is created.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <BsButton variant="secondary" type="button" onClick={handleCloseAndReset} disabled={submitting}>
            Cancel
          </BsButton>
          <Button
            variant="primary"
            type="submit"
            loading={submitting}
            loadingPosition="start"
            disabled={submitting || !description.trim()}
            style={{ minWidth: '6.25rem' }}
          >
            Create
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ApiTokenAddForm;
